import React, {useEffect, useRef, useState} from 'react';
import {PASSWORD_REQUIREMENTS, validateStrongPassword} from '@common/validation/strongPassword';
import {CODE_PERMISSIONS} from '@common/permissions';
import {useAuthStore} from '@common-web/stores/authStore';
import {useHasPermission} from '@common-web/hooks/use-permissions';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {NotificationSettings} from '@common-web/components/notification-settings';
import {PageHeader} from '@common-web/components/page-header';
import {User, Settings, Shield, Loader2, Camera, KeyRound} from 'lucide-react';
import {AuthGuard} from '@common-web/components/auth-guard';
import {Button} from '@common-web/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Avatar, AvatarFallback, AvatarImage} from '@common-web/components/ui/avatar';
import {trpc} from '@common-web/lib/trpc';
import {useToast} from '@common-web/hooks/use-toast';
import {useUserAvatarUrl} from '@common-web/hooks/useUserAvatarUrl';
import {
  splitFullName,
  joinFullName,
  initialsFromParts,
  initialsFromFullName,
} from '@common-web/lib/nameUtils';

export function SettingsPage() {
  return (
    <AuthGuard>
      <SettingsPageContent />
    </AuthGuard>
  );
}

function SettingsPageContent() {
  const user = useAuthStore((state) => state.user);
  const refreshUser = useAuthStore((state) => state.refreshUser);
  const canManageNotifications = useHasPermission(CODE_PERMISSIONS.notifications.manageOwn.key);
  const {toast} = useToast();
  const utils = trpc.useUtils();

  const [profileOpen, setProfileOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  const {avatarUrl: avatarDisplayUrl} = useUserAvatarUrl();

  const getAvatarUploadUrlMutation = trpc.user.profile.getAvatarUploadUrl.useMutation();
  const saveAvatarMutation = trpc.user.profile.saveAvatar.useMutation();
  const updateNameMutation = trpc.user.profile.updateName.useMutation({
    onSuccess: async () => {
      await refreshUser();
      toast({
        title: 'Profile updated',
        description: 'Your account information has been saved.',
      });
      setProfileOpen(false);
    },
    onError: (err) => {
      toast({
        title: 'Could not save profile',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  const changePasswordMutation = trpc.user.profile.changePassword.useMutation({
    onSuccess: () => {
      toast({
        title: 'Password updated',
        description: 'Your password has been changed.',
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    },
    onError: (err) => {
      toast({
        title: 'Could not change password',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  useEffect(() => {
    if (!profileOpen || !user) return;
    const parts = splitFullName(user.name ?? '');
    setFirstName(parts.firstName);
    setLastName(parts.lastName);
  }, [profileOpen, user]);

  const handleAvatarFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file',
        description: 'Please choose an image file.',
        variant: 'destructive',
      });
      return;
    }

    setIsUploadingAvatar(true);
    try {
      const {uploadUrl, key} = await getAvatarUploadUrlMutation.mutateAsync({
        fileName: file.name,
        contentType: file.type,
      });

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {'Content-Type': file.type},
      });

      if (!uploadResponse.ok) {
        throw new Error('Upload failed');
      }

      await saveAvatarMutation.mutateAsync({key});
      await refreshUser();
      await utils.user.profile.getAvatarUrl.invalidate();
      toast({
        title: 'Photo updated',
        description: 'Your profile photo has been saved.',
      });
    } catch {
      toast({
        title: 'Upload failed',
        description: 'Could not update your profile photo.',
        variant: 'destructive',
      });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleSaveFullName = () => {
    const name = joinFullName(firstName, lastName);
    if (!name.trim()) {
      toast({
        title: 'Name required',
        description: 'Enter at least a first or last name.',
        variant: 'destructive',
      });
      return;
    }

    updateNameMutation.mutate({name});
  };

  const handleChangePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword) {
      toast({
        title: 'Current password required',
        description: 'Enter your current password to continue.',
        variant: 'destructive',
      });
      return;
    }
    const strength = validateStrongPassword(newPassword);
    if (strength !== true) {
      toast({
        title: 'Invalid new password',
        description: strength,
        variant: 'destructive',
      });
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'New password and confirmation must match.',
        variant: 'destructive',
      });
      return;
    }
    changePasswordMutation.mutate({
      currentPassword,
      newPassword,
      confirmNewPassword,
    });
  };

  if (!user) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <p>Please log in to access settings</p>
      </div>
    );
  }

  const dialogInitials = initialsFromParts(firstName, lastName);
  const cardInitials = initialsFromFullName(user.name ?? '');

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        <PageHeader
          title='Account Settings'
          description='Manage your account preferences and notification settings'
          icon={<Settings className='h-6 w-6' />}
        />
        <div className='px-8 py-6 max-w-4xl mx-auto w-full space-y-6'>
          <Card>
            <CardHeader className='flex flex-row flex-wrap items-start justify-between gap-4 space-y-0'>
              <CardTitle className='flex items-center gap-2'>
                <User className='h-5 w-5' />
                Account Information
              </CardTitle>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() => setProfileOpen(true)}
              >
                Edit profile
              </Button>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='flex flex-col gap-4 sm:flex-row sm:items-start'>
                <Avatar className='h-16 w-16'>
                  {avatarDisplayUrl && <AvatarImage src={avatarDisplayUrl} alt={user.name ?? ''} />}
                  <AvatarFallback>{cardInitials.slice(0, 2)}</AvatarFallback>
                </Avatar>
                <div className='grid flex-1 grid-cols-1 gap-4 md:grid-cols-2'>
                  <div>
                    <label className='text-sm font-medium text-muted-foreground'>Full Name</label>
                    <p className='text-sm font-medium'>{user.name}</p>
                  </div>
                  <div>
                    <label className='text-sm font-medium text-muted-foreground'>
                      Email Address
                    </label>
                    <p className='text-sm font-medium'>{user.email}</p>
                  </div>
                </div>
              </div>

              <div className='flex flex-wrap items-center gap-4 pt-2'>
                <div className='flex items-center gap-2'>
                  <Shield className='h-4 w-4 text-muted-foreground' />
                  <span className='text-sm text-muted-foreground'>Account Type:</span>
                  {user.cbsInternalProfile ? (
                    <Badge variant='default'>Internal User</Badge>
                  ) : (
                    <Badge variant='secondary'>Client User</Badge>
                  )}
                </div>
                {user.cbsInternalProfile?.internalRole && (
                  <div className='flex items-center gap-2'>
                    <span className='text-sm text-muted-foreground'>Role:</span>
                    <Badge variant='outline'>
                      {user.cbsInternalProfile.internalRole.replace('internal:', '')}
                    </Badge>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <KeyRound className='h-5 w-5' />
                Security
              </CardTitle>
              <CardDescription>Change password</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePasswordSubmit} className='space-y-4 max-w-md'>
                <div className='space-y-2'>
                  <Label htmlFor='settings-current-password'>Current password</Label>
                  <Input
                    id='settings-current-password'
                    type='password'
                    autoComplete='current-password'
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='settings-new-password'>New password</Label>
                  <Input
                    id='settings-new-password'
                    type='password'
                    autoComplete='new-password'
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <ul className='list-disc pl-4 text-xs text-muted-foreground'>
                    {PASSWORD_REQUIREMENTS.map((requirement) => (
                      <li key={requirement}>{requirement}</li>
                    ))}
                  </ul>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='settings-confirm-password'>Confirm new password</Label>
                  <Input
                    id='settings-confirm-password'
                    type='password'
                    autoComplete='new-password'
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                  />
                </div>
                <Button type='submit' disabled={changePasswordMutation.isPending}>
                  {changePasswordMutation.isPending ? (
                    <>
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                      Updating…
                    </>
                  ) : (
                    'Update password'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
            <DialogContent className='sm:max-w-md' onOpenAutoFocus={(e) => e.preventDefault()}>
              <DialogHeader>
                <DialogTitle>Edit profile</DialogTitle>
                <DialogDescription>
                  Update your name and profile photo. Changes apply across your account.
                </DialogDescription>
              </DialogHeader>

              <div className='space-y-6 py-2'>
                <div className='flex flex-col items-center gap-3'>
                  <div className='relative'>
                    <Avatar className='h-24 w-24'>
                      {avatarDisplayUrl && (
                        <AvatarImage src={avatarDisplayUrl} alt={user.name ?? ''} />
                      )}
                      <AvatarFallback className='text-lg'>
                        {dialogInitials.slice(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    {isUploadingAvatar && (
                      <div className='absolute inset-0 flex items-center justify-center rounded-full bg-background/80'>
                        <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
                      </div>
                    )}
                  </div>
                  <input
                    ref={avatarInputRef}
                    type='file'
                    accept='image/*'
                    className='sr-only'
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (f) void handleAvatarFile(f);
                    }}
                  />
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    disabled={isUploadingAvatar}
                    onClick={() => avatarInputRef.current?.click()}
                  >
                    <Camera className='mr-2 h-4 w-4' />
                    Upload photo
                  </Button>
                </div>

                <div className='grid gap-4 sm:grid-cols-2'>
                  <div className='space-y-2'>
                    <Label htmlFor='settings-first-name'>First name</Label>
                    <Input
                      id='settings-first-name'
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      autoComplete='given-name'
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='settings-last-name'>Last name</Label>
                    <Input
                      id='settings-last-name'
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      autoComplete='family-name'
                    />
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button type='button' variant='outline' onClick={() => setProfileOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type='button'
                  onClick={handleSaveFullName}
                  disabled={updateNameMutation.isPending}
                >
                  {updateNameMutation.isPending ? (
                    <>
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                      Saving…
                    </>
                  ) : (
                    'Save'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {canManageNotifications && <NotificationSettings showTitle={true} />}
        </div>
      </div>
    </div>
  );
}
